import React, { memo, useEffect, useRef, useState } from 'react'

import { Icon, Text, Copy, classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { string } from 'prop-types'

import { iconSize, iconSizeMedium } from 'Pods/ThreeDStudio/constants'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'

const textIcon = <Icon svgComponent={Text} size={iconSizeMedium} notSpaced />
const copyIcon = <Icon svgComponent={Copy} size={iconSize} notSpaced />

// Bottom-left control over the edit canvas, shown only for prompt-edited versions.
// Toggles an info card with the instruction that produced the version and a copy
// button. Closes on outside click or Escape.
function PromptViewer({ prompt }) {
  const t = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [isCopied, setIsCopied] = useState(false)
  const rootRef = useRef(null)

  useEffect(() => {
    if (!isOpen) {
      return undefined
    }
    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setIsOpen(false)
      }
    }
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }
    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  const handleToggle = () => {
    setIsCopied(false)
    setIsOpen(prev => !prev)
  }

  const handleCopy = () => {
    navigator.clipboard?.writeText(prompt)
      .then(() => {
        setIsCopied(true)
        if (typeof window !== 'undefined' && window.showNotification) {
          window.showNotification(
            t('imageAssistantPanel.editTab.promptViewer.copiedNotification'),
            { type: 'info', autohide: true },
          )
        }
      })
      .catch(() => {})
  }

  return (
    <div className="image-assistant-edit-tab__prompt-viewer" ref={rootRef}>
      <button
        type="button"
        className={classnames(
          'image-assistant-edit-tab__prompt-toggle',
          isOpen && 'image-assistant-edit-tab__prompt-toggle--open',
        )}
        aria-label={t('imageAssistantPanel.editTab.promptViewer.toggleLabel')}
        aria-expanded={isOpen}
        onClick={handleToggle}
      >
        {textIcon}
      </button>
      {isOpen && (
        <div className="image-assistant-edit-tab__prompt-card">
          <p className="image-assistant-edit-tab__prompt-text cgt-caption">{prompt}</p>
          <button
            type="button"
            className="image-assistant-edit-tab__prompt-copy"
            aria-label={isCopied
              ? t('imageAssistantPanel.editTab.promptViewer.copiedLabel')
              : t('imageAssistantPanel.editTab.promptViewer.copyLabel')}
            onClick={handleCopy}
          >
            {copyIcon}
          </button>
        </div>
      )}
    </div>
  )
}

PromptViewer.propTypes = {
  prompt: string.isRequired,
}

export default memo(PromptViewer)
